import React from 'react';
import type { CSSProperties } from 'react';
import { Plus } from 'lucide-react';
import { postToBuilder } from '../../editor/editorChannel';
import { insertMessage } from '../../editor/editorIntent';
import { CHROME_CLASS, HOLD_CLASS } from '../../editor/editorVisit';
import { isBuilderPage } from './ElementPlaceholder';

// What an empty container offers the merchant, which until now was nothing at all.
//
// A container with no children draws no box: it collapses to zero pixels, so there is nothing to point
// at, nothing to drop into, and no way back out of it — a merchant who adds a Column and then wants
// something in it is looking at a gap in the page with no way to say "here". LightFunnels answers this
// with a dashed slot spelling "+ Add Element" inside every empty box, and that slot is both the target
// and the invitation. This is ours.
//
// It performs nothing. Pressing it posts the same `insert` the `+` between two nodes posts, naming this
// container and its first position, and the builder opens the library there — the canvas never owns the
// draft. It is chrome, so the hit test never mistakes it for a node of the page, and it holds the hover
// so reaching for it does not take the box it belongs to off the screen first.
//
// The press is stopped where it happens: one container in the catalog is a whole box that is one link,
// and inside that one the slot sits in an `<a>` — an unstopped press would follow it and take the
// merchant out of the builder rather than open the library.

/** Plain scaffolding, in no colour or font a theme owns — the same rule the empty-element outline keeps. */
const SLOT: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  minHeight: 56,
  padding: '10px 12px',
  border: '1px dashed currentColor',
  borderRadius: 6,
  background: 'transparent',
  color: 'inherit',
  opacity: 0.4,
  font: 'inherit',
  fontSize: 13,
  lineHeight: 1.4,
  cursor: 'pointer'
};

// Draws the invitation inside one empty container, and nothing at all to a shopper.
export const ElementEmptySlot: React.FC<{ parentId: string }> = ({ parentId }) => {
  if (!isBuilderPage()) return null;
  const title = 'Add an element here';
  return (
    <button
      type="button"
      className={`xs-editor-slot ${CHROME_CLASS} ${HOLD_CLASS}`}
      style={SLOT}
      title={title}
      aria-label={title}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        postToBuilder(insertMessage({ kind: 'addHere', parentId, index: 0 }));
      }}
    >
      <Plus size={14} aria-hidden />
      Add element
    </button>
  );
};
